import { useState, useCallback } from 'react';
import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { route } from '../../utils/route';
import { openChat as openSupportChat } from '../../utils/chat';

interface PrivacyProps {
  isAuthenticated: boolean;
  crispChatUrl: string;
  currentUrl: string;
  lastUpdated: string;
}

// Icons as components for cleaner JSX
const ShieldIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const DatabaseIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const MonitorIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const CartIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const MailIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ShareIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const MessageIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const MapPinIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ExternalLinkIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

export default function Privacy({ isAuthenticated, crispChatUrl, currentUrl, lastUpdated }: PrivacyProps) {
  const [chatOpened, setChatOpened] = useState(false);

  const openChat = useCallback(() => {
    // Use the new utility function with fallback support
    openSupportChat('Hola, tengo una consulta sobre la política de privacidad.');
    setChatOpened(true);
    setTimeout(() => setChatOpened(false), 2000);
  }, []);

  const pageTitle = 'Política de Privacidad | Abasto Virtual Córdoba';
  const pageDescription = 'Conocé cómo protegemos tu información personal en Abasto Virtual. Política de privacidad transparente para nuestros clientes de frutas y verduras en Córdoba.';

  return (
    <MainLayout>
      <Head title={pageTitle}>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="política privacidad, protección datos, abasto virtual córdoba, información personal, cookies, email, teléfono" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content="/appicons/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="/appicons/og-image.jpg" />
        <link rel="canonical" href={currentUrl} />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16 pt-8 md:pt-12">
        <div className="mx-auto max-w-3xl px-4">
          {/* Header */}
          <div className="mb-10 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-green-100">
              <ShieldIcon className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="mb-3 text-3xl font-bold text-gray-900 md:text-4xl">
              Política de Privacidad
            </h1>
            <p className="text-lg text-gray-500">
              Cómo protegemos tu información personal
            </p>
          </div>

          {/* Intro card */}
          <div className="mb-10 rounded-2xl border-l-4 border-green-500 bg-green-50 p-6">
            <p className="text-base leading-relaxed text-green-900">
              Esta Política de privacidad describe cómo se recopila, utiliza y comparte tu información personal
              cuando visitás o realizás una compra en <strong>abastovirtual.com</strong>.
            </p>
          </div>

          {/* Content sections */}
          <div className="space-y-10">
            {/* Section: Info we collect */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
                  <DatabaseIcon className="h-5 w-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Información que Recopilamos</h2>
              </div>

              <div className="space-y-6">
                {/* Device info */}
                <div className="rounded-xl border border-gray-200 bg-white p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <MonitorIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="font-semibold text-gray-900">Información del Dispositivo</h3>
                  </div>
                  <p className="mb-4 text-gray-600">
                    Cuando visitás el sitio, recopilamos automáticamente información sobre tu navegador,
                    dirección IP, zona horaria y cookies instaladas. También registramos las páginas que visitás
                    y cómo interactuás con el sitio.
                  </p>
                  <div className="rounded-lg bg-gray-50 p-4">
                    <p className="mb-2 text-sm font-medium text-gray-700">Tecnologías que usamos:</p>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                        <span><strong>Cookies:</strong> Archivos de datos con identificadores únicos. <a href="http://www.allaboutcookies.org" target="_blank" rel="noopener" className="text-green-600 hover:underline">Más info</a></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                        <span><strong>Archivos de registro:</strong> Rastrean acciones, IP, navegador y páginas visitadas.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Order info */}
                <div className="rounded-xl border border-gray-200 bg-white p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <CartIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="font-semibold text-gray-900">Información de Pedido</h3>
                  </div>
                  <p className="text-gray-600">
                    Cuando realizás una compra, recopilamos tu nombre, dirección de facturación y envío,
                    información de pago, email y teléfono.
                  </p>
                </div>
              </div>
            </section>

            {/* Section: How we use it */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
                  <CheckIcon className="h-5 w-5 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">¿Cómo Usamos tu Información?</h2>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h4 className="mb-3 font-semibold text-gray-900">Información de pedido</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-500" />
                      Procesar y cumplir tu pedido
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-500" />
                      Comunicarnos sobre tu pedido
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-500" />
                      Detectar riesgos o fraudes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-500" />
                      Enviarte ofertas (según preferencias)
                    </li>
                  </ul>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h4 className="mb-3 font-semibold text-gray-900">Información del dispositivo</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-500" />
                      Detectar riesgos y fraudes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-500" />
                      Mejorar y optimizar el sitio
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4 text-green-500" />
                      Generar análisis de navegación
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section: Email & Phone */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-100">
                  <MailIcon className="h-5 w-5 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Uso de Email y Teléfono</h2>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <MailIcon className="h-5 w-5 text-red-500" />
                    <h4 className="font-semibold text-gray-900">Email</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                      Informarte sobre el estado de tu pedido
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                      Envío semanal de descuentos (podés desubscribirte)
                    </li>
                  </ul>
                </div>

                <div className="rounded-xl border-2 border-green-200 bg-green-50 p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <PhoneIcon className="h-5 w-5 text-green-600" />
                    <h4 className="font-semibold text-green-800">Teléfono</h4>
                  </div>
                  <div className="mb-3 flex items-center gap-2 rounded-lg bg-green-100 px-3 py-2">
                    <ShieldIcon className="h-4 w-4 text-green-700" />
                    <span className="text-sm font-medium text-green-800">
                      Nos comprometemos a NUNCA usar tu teléfono para marketing
                    </span>
                  </div>
                  <p className="text-sm text-green-700">
                    <strong>Uso exclusivo:</strong> Comunicación inmediata solo si hay problemas
                    con tu pedido (ej: el repartidor no encuentra tu domicilio).
                  </p>
                </div>
              </div>
            </section>

            {/* Section: Sharing */}
            <section>
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100">
                  <ShareIcon className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Compartir Información</h2>
              </div>

              <p className="mb-4 text-gray-600">
                Compartimos tu información con terceros para brindarte nuestro servicio:
              </p>

              <div className="space-y-3">
                <a
                  href="https://www.mercadopago.com.ar/privacidad"
                  target="_blank"
                  rel="noopener"
                  className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50"
                >
                  <div>
                    <h4 className="font-semibold text-sky-600">Mercado Pago</h4>
                    <p className="text-sm text-gray-500">Procesamiento seguro de pagos</p>
                  </div>
                  <ExternalLinkIcon className="h-5 w-5 text-gray-400" />
                </a>

                <a
                  href="https://www.google.com/intl/en/policies/privacy/"
                  target="_blank"
                  rel="noopener"
                  className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50"
                >
                  <div>
                    <h4 className="font-semibold text-blue-600">Google Analytics</h4>
                    <p className="text-sm text-gray-500">
                      Análisis de uso del sitio · <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener" className="text-green-600 hover:underline">Opt-out</a>
                    </p>
                  </div>
                  <ExternalLinkIcon className="h-5 w-5 text-gray-400" />
                </a>
              </div>

              <div className="mt-4 rounded-xl bg-amber-50 p-4">
                <p className="text-sm text-amber-800">
                  <strong>Cumplimiento legal:</strong> También podemos compartir información para cumplir
                  con leyes aplicables o proteger nuestros derechos.
                </p>
              </div>
            </section>

            {/* Section: Other policies */}
            <section>
              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h3 className="mb-2 font-semibold text-gray-900">Do Not Track</h3>
                  <p className="text-sm text-gray-600">
                    No modificamos nuestras prácticas cuando detectamos una señal "Do Not Track" del navegador.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h3 className="mb-2 font-semibold text-gray-900">Retención de Datos</h3>
                  <p className="text-sm text-gray-600">
                    Mantenemos tu información de pedido hasta que solicites eliminarla.
                  </p>
                </div>

                <div className="rounded-xl border border-red-100 bg-red-50 p-5">
                  <h3 className="mb-2 font-semibold text-red-800">Menores de Edad</h3>
                  <p className="text-sm text-red-700">
                    Este sitio no está destinado a personas menores de edad.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h3 className="mb-2 font-semibold text-gray-900">Cambios a esta Política</h3>
                  <p className="text-sm text-gray-600">
                    Podemos actualizar esta política ocasionalmente por razones operativas, legales o reglamentarias.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact section */}
            <section className="rounded-2xl bg-blue-50 p-6">
              <div className="mb-4 flex items-center gap-2">
                <MessageIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-bold text-blue-900">¿Tenés preguntas sobre privacidad?</h3>
              </div>
              <p className="mb-5 text-blue-800">
                Si tenés consultas sobre nuestras prácticas de privacidad, estamos disponibles para ayudarte.
              </p>
              <button
                type="button"
                onClick={openChat}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold transition-all ${
                  chatOpened
                    ? 'bg-green-600 text-white'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {chatOpened ? (
                  <>
                    <CheckIcon className="h-5 w-5" />
                    Chat Abierto
                  </>
                ) : (
                  <>
                    <MessageIcon className="h-5 w-5" />
                    Iniciar Chat en Vivo
                  </>
                )}
              </button>
            </section>

            {/* Footer info */}
            <div className="space-y-4">
              <div className="rounded-xl bg-gray-100 p-5">
                <div className="mb-2 flex items-center gap-2">
                  <MapPinIcon className="h-5 w-5 text-gray-500" />
                  <h4 className="font-semibold text-gray-700">Información de Contacto</h4>
                </div>
                <p className="text-sm text-gray-600">
                  De La Semillería 1731 - Ampl. Palmar, Córdoba, 5012, Argentina
                </p>
              </div>

              <div className="rounded-xl bg-gray-50 py-4 text-center">
                <p className="text-sm text-gray-500">
                  Última actualización: {lastUpdated}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Política de Privacidad',
            description: 'Política de privacidad de Abasto Virtual para protección de datos personales',
            url: currentUrl,
            mainEntity: {
              '@type': 'PrivacyPolicy',
              name: 'Política de Privacidad de Abasto Virtual',
              publisher: {
                '@type': 'Organization',
                name: 'Abasto Virtual',
                url: 'https://www.abastovirtual.com',
              },
            },
          }),
        }}
      />
    </MainLayout>
  );
}
